import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ProjectTabs from "../Tabs/Tabs";
import DetailsCards from "./DetailsCards";

export default function Details({ projectData, refetch }: any) {
  const Asset = useRef(null);
  const General = useRef(null);
  const Valuation = useRef(null);
  const Financing = useRef(null);
  const Documents = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [userClickedTab, setUserClickedTab] = useState(false);
  const location = useLocation();
  const tabList = ["General", "Asset", "Valuation", "Financing", "Documents"];
  const clickTimeoutRef = useRef<number | null>(null);

  const handleScrollToSection = (currentTab: any) => {
    let ref: React.RefObject<HTMLDivElement> = { current: null };
    if (currentTab === "General") {
      ref = General;
    } else if (currentTab === "Asset") {
      ref = Asset;
    } else if (currentTab === "Valuation") {
      ref = Valuation;
    } else if (currentTab === "Financing") {
      ref = Financing;
    } else {
      ref = Documents;
    }
    const currentIndex = tabList.indexOf(currentTab);
    setActiveIndex(currentIndex);
    
    // Set a flag to indicate user clicked a tab
    setUserClickedTab(true);
    
    // Clear any existing timeout
    if (clickTimeoutRef.current) {
      window.clearTimeout(clickTimeoutRef.current);
    }
    
    // Set a timeout to reset the flag after scrolling completes
    clickTimeoutRef.current = window.setTimeout(() => {
      setUserClickedTab(false);
    }, 1000); // 1 second delay

    if (ref && ref.current) {
      window.scrollTo({
        top: ref.current.offsetTop - 200,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (location.state && location.state.fromDocumentStatusUpdate) {
      handleScrollToSection("Documents");
    }
    // eslint-disable-next-line
  }, [location.state]);

  useEffect(() => {
    const handleIntersection = (entries: any) => {
      // If user just clicked a tab, don't update based on scroll
      if (userClickedTab) return;
      
      // Sort entries by their intersection ratio in descending order
      const visibleEntries = entries
        .filter((entry: any) => entry.isIntersecting)
        .sort((a: any, b: any) => b.intersectionRatio - a.intersectionRatio);
      
      // Use the most visible entry to determine the active tab
      if (visibleEntries.length > 0) {
        const mostVisibleEntry = visibleEntries[0];
        const currentIndex = tabList.indexOf(mostVisibleEntry.target.dataset.section);
        setActiveIndex(currentIndex);
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "-100px 0px -100px 0px", // Adjust rootMargin to be more precise
      threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], // Multiple thresholds for better accuracy
    });

    if (General.current) observer.observe(General.current);
    if (Asset.current) observer.observe(Asset.current);
    if (Valuation.current) observer.observe(Valuation.current);
    if (Financing.current) observer.observe(Financing.current);
    if (Documents.current) observer.observe(Documents.current);

    return () => {
      // eslint-disable-next-line
      if (General.current) observer.unobserve(General.current);
      // eslint-disable-next-line
      if (Asset.current) observer.unobserve(Asset.current);
      // eslint-disable-next-line
      if (Valuation.current) observer.unobserve(Valuation.current);
      // eslint-disable-next-line
      if (Financing.current) observer.unobserve(Financing.current);
      // eslint-disable-next-line
      if (Documents.current) observer.unobserve(Documents.current);
      
      // Clear timeout on cleanup
      if (clickTimeoutRef.current) {
        window.clearTimeout(clickTimeoutRef.current);
      }
    };
    // eslint-disable-next-line
  }, [userClickedTab]);

  return (
    <div>
      <ProjectTabs
        activeIndex={activeIndex}
        onSelectTab={handleScrollToSection}
        tabList={tabList}
        uniqueId="detail-tab-component"
        classNames="sticky top-[60px] bg-[#f7f5f2] z-[9]"
      />
      <DetailsCards refs={{ Documents, Financing, Valuation, General, Asset }} data={projectData} refetch={refetch} />
    </div>
  );
}
